import { useEffect, useState, Fragment } from "react";
import client from "../lib/api";
import { money, DUE_FILTERS, dueOf, daysOverdue, applyDueView, fmtDate } from "../lib/helpers";
import { useAuth } from "../context/AuthContext";
import { CurrencyDollar, CheckCircle, Clock, CreditCard, Heart, WarningCircle, PencilSimple, Lock, Plus, FilePdf, CalendarBlank, ArrowsDownUp, MagnifyingGlass, CaretDown, CaretUp } from "@phosphor-icons/react";
import { RotateCcw } from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Switch } from "./ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { toast } from "sonner";
import TestPaymentTools from "./TestPaymentTools";

const TIP_PRESETS = [25, 50, 100];

const BILL_TONES = {
  emerald: { cls: "surface-kpi--emerald", medal: "bg-[#E6EFEA] text-[#1A4331] border-[#2D6A4F]/30", value: "#1A4331" },
  crimson: { cls: "surface-kpi--crimson", medal: "bg-[#F0E6E6] text-[#7A0000] border-[#7E1119]/30", value: "#7A0000" },
  bronze: { cls: "surface-kpi--bronze", medal: "bg-[#F5ECE3] text-[#8A5028] border-[#B87333]/35", value: "#8A5028" },
  oxblood: { cls: "surface-kpi--oxblood-accent", medal: "bg-[#4A0000]/8 text-[#4A0000] border-[#4A0000]/20", value: "#4A0000" },
};
function Stat({ label, value, icon: Icon, variant = "oxblood" }) {
  const t = BILL_TONES[variant] || BILL_TONES.oxblood;
  return (
    <div className={`surface-kpi ${t.cls} p-6 flex flex-col gap-3`}>
      <div className="flex items-center justify-between relative z-10">
        <span className="text-[#5C5A58] eyebrow">{label}</span>
        <span className={`h-8 w-8 rounded-full flex items-center justify-center border ${t.medal}`}><Icon size={16} weight="duotone" /></span>
      </div>
      <p className="font-display text-3xl tabular-nums relative z-10" style={{ color: t.value }}>{money(value)}</p>
    </div>
  );
}

export default function BillingTable() {
  const { user } = useAuth();
  const isAgent = user?.role === "agent";
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [payRow, setPayRow] = useState(null);       // row being paid (agent tip dialog)
  const [tipChoice, setTipChoice] = useState(0);     // preset value or "custom"
  const [customTip, setCustomTip] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [discInfo, setDiscInfo] = useState(null);    // { valid, discount_amount, new_total, message }
  const [discChecking, setDiscChecking] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({ package_price: "", client_paid: false });
  const [adjustRow, setAdjustRow] = useState(null);
  const [adjType, setAdjType] = useState("surcharge");
  const [adjAmount, setAdjAmount] = useState("");
  const [adjReason, setAdjReason] = useState("");
  const [savingAdj, setSavingAdj] = useState(false);
  const [invoicing, setInvoicing] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [savedCards, setSavedCards] = useState([]);
  const [selectedPm, setSelectedPm] = useState("new");
  const [saveCard, setSaveCard] = useState(true);
  const [refundRow, setRefundRow] = useState(null);
  const [refunding, setRefunding] = useState(null);
  const [dueMode, setDueMode] = useState("all");
  const [dueSort, setDueSort] = useState("none");
  const [invSearch, setInvSearch] = useState("");
  const [viewMode, setViewMode] = useState(user?.role === "agent" ? "invoice" : "agent");
  const [expanded, setExpanded] = useState(new Set());

  const load = () => client.get("/billing").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const tipAmount = tipChoice === "custom" ? Math.max(0, parseFloat(customTip) || 0) : tipChoice;

  // Live-validate a discount code against the row being paid.
  useEffect(() => {
    if (!payRow) return;
    const code = discountCode.trim();
    if (!code) { setDiscInfo(null); return; }
    let active = true;
    setDiscChecking(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await client.post("/discounts/validate", { code, property_id: payRow.property_id });
        if (active) setDiscInfo(data.valid
          ? { valid: true, discount_amount: data.discount_amount, new_total: data.new_total, message: `${data.kind === "percent" ? data.value + "% off" : "$" + data.value + " off"} applied` }
          : { valid: false, message: data.message });
      } catch (e) { if (active) setDiscInfo({ valid: false, message: e.response?.data?.detail || "Couldn't check code" }); }
      finally { if (active) setDiscChecking(false); }
    }, 400);
    return () => { active = false; clearTimeout(t); };
  }, [discountCode, payRow]);

  const netOf = (row) => Math.max(0, (row.package_price || 0) - (row.discount_amount || 0));
  const dialogNet = discInfo?.valid ? Math.max(0, discInfo.new_total) : (payRow ? netOf(payRow) : 0);

  const checkout = async (pid, tip, code, save) => {
    setBusy(pid);
    try {
      const r = await client.post("/payments/checkout", { property_id: pid, origin_url: window.location.origin, tip, discount_code: code || null, save_card: !!save });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start payment");
      setBusy(null);
    }
  };

  // Agents pick a tip first; admins pay directly with no tip.
  const startPay = (row) => {
    if (isAgent) {
      setPayRow(row);
      setTipChoice(0);
      setCustomTip("");
      setDiscountCode(row.discount_code || "");
      setDiscInfo(null);
      setSaveCard(true);
      setSelectedPm("new");
      setSavedCards([]);
      client.get("/payments/methods").then(({ data }) => {
        const cards = data.cards || [];
        setSavedCards(cards);
        if (cards.length) setSelectedPm(cards[0].id);
      }).catch(() => {});
    } else {
      checkout(row.property_id, 0, row.discount_code || null, false);
    }
  };

  const confirmPay = async () => {
    const pid = payRow.property_id;
    const code = discInfo?.valid ? discountCode.trim().toUpperCase() : null;
    if (isAgent && selectedPm && selectedPm !== "new") {
      setBusy(pid);
      try {
        const r = await client.post("/payments/pay-saved", { property_id: pid, payment_method_id: selectedPm, tip: tipAmount, discount_code: code });
        if (r.data.status === "paid") { setPayRow(null); setBusy(null); toast.success("Payment complete — thank you!"); load(); return; }
        toast.message(r.data.reason || "Taking you to secure checkout…");
        setPayRow(null);
        checkout(pid, tipAmount, code, false);
      } catch (e) {
        setBusy(null);
        toast.error(e.response?.data?.detail || "Could not charge card");
      }
      return;
    }
    setPayRow(null);
    checkout(pid, tipAmount, code, saveCard);
  };

  // Admin: edit the incoming invoice directly (price override + manual mark paid/unpaid).
  const openEdit = (r) => {
    setEditForm({ package_price: String(r.package_price || 0), client_paid: !!r.paid });
    setEditRow(r);
  };
  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await client.patch(`/properties/${editRow.property_id}`, {
        package_price: Math.max(0, parseFloat(editForm.package_price) || 0),
        client_paid: editForm.client_paid,
      });
      toast.success("Invoice updated");
      setEditRow(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update invoice"); }
    finally { setSavingEdit(false); }
  };

  // Admin: refund a paid invoice back to the client.
  const doRefund = async () => {
    const pid = refundRow.property_id;
    setRefunding(pid);
    try {
      const r = await client.post("/payments/refund", { property_id: pid });
      toast.success(r.data.stripe ? "Refunded via Stripe" : "Invoice marked refunded");
      setRefundRow(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not refund"); }
    finally { setRefunding(null); }
  };

  const openAdjust = (r) => { setAdjType("surcharge"); setAdjAmount(""); setAdjReason(""); setAdjustRow(r); };
  const doAdjust = async () => {
    const amt = Math.round((parseFloat(adjAmount) || 0) * 100) / 100;
    if (amt <= 0) return toast.error("Enter an amount greater than $0");
    if (!adjReason.trim()) return toast.error("A reason is required");
    setSavingAdj(true);
    try {
      await client.post(`/finance/receivables/${adjustRow.property_id}/adjust`, { type: adjType, amount: amt, reason: adjReason.trim() });
      toast.success(`${adjType === "surcharge" ? "Surcharge" : "Discount"} of ${money(amt)} applied`);
      setAdjustRow(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not apply adjustment"); }
    finally { setSavingAdj(false); }
  };

  const downloadInvoice = async (r) => {
    setInvoicing(r.property_id);
    try {
      const inv = await client.post(`/finance/receivables/${r.property_id}/invoice`, {});
      const res = await client.get(`/finance/receivables/${r.property_id}/invoice.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${inv.data.invoice_number || "invoice"}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Invoice ${inv.data.invoice_number} downloaded`);
      load();
    } catch (e) { toast.error("Could not generate invoice"); }
    finally { setInvoicing(null); }
  };

  const downloadStatement = async (g) => {
    if (!g.agent_id) return toast.error("No agent on these invoices");
    try {
      const res = await client.get(`/finance/agents/${g.agent_id}/statement.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `statement-${(g.agent || "agent").replace(/\s+/g, "-")}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Statement for ${g.agent} downloaded`);
    } catch (e) { toast.error("Could not generate statement"); }
  };

  if (!data) return <p className="text-[#5C5A58]">Loading…</p>;

  const q = invSearch.trim().toLowerCase();
  const visibleRows = applyDueView(data.incoming || [], dueMode, dueSort).filter((r) => {
    if (!q) return true;
    return (r.invoice_number || "").toLowerCase().includes(q)
      || (r.title || "").toLowerCase().includes(q)
      || (r.agent || "").toLowerCase().includes(q);
  });

  const renderInvoiceRow = (r, i) => (
    <tr key={r.property_id} className={`${i % 2 ? "bg-[#FBFAF7]" : "bg-white"} ${r.paid ? "row-paid" : r.overdue ? "row-overdue" : ""} ${r.overdue ? "border-l-4 border-[#E01E37]" : ""}`} data-testid={`billing-row-${r.property_id}`}>
      <td className="p-4 font-medium">{r.title}</td>
      <td className="p-4">{r.agent}</td>
      <td className="p-4">
        {r.discount_amount > 0 ? (
          <>
            <div className="font-mono-num font-semibold" data-testid={`billing-net-${r.property_id}`}>{money(netOf(r))}</div>
            <div className="text-xs text-[#5C5A58]"><span className="line-through">{money(r.package_price)}</span> <span className="text-[#2e7d32]">· {r.discount_code} −{money(r.discount_amount)}</span></div>
          </>
        ) : (
          <div className="font-mono-num font-semibold">{money(r.package_price)}</div>
        )}
        {r.package_type && <div className="text-xs text-[#5C5A58]">{r.package_type}{r.drone ? " + drone" : ""}</div>}
        {r.paid && r.tip > 0 && <div className="text-xs text-[#2e7d32] flex items-center gap-1 mt-0.5"><Heart size={11} weight="fill" />+{money(r.tip)} tip</div>}
      </td>
      <td className="p-4" data-testid={`billing-due-${r.property_id}`}>
        {dueOf(r) ? (
          <>
            <div className="text-sm">{fmtDate(dueOf(r))}</div>
            {!r.paid && !r.refunded && daysOverdue(r) > 0 && <div className="text-xs text-[#E01E37]">{daysOverdue(r)}d overdue</div>}
            {!r.due_date && <div className="text-xs text-[#9A968E]">from shoot</div>}
          </>
        ) : <span className="text-[#9A968E] text-sm">—</span>}
      </td>
      <td className="p-4">
        {r.paid
          ? <span className="pill pill--paid">Paid</span>
          : r.refunded
            ? <span data-testid={`billing-refunded-${r.property_id}`} className="pill pill--partial">Refunded</span>
            : r.overdue
              ? <span data-testid={`billing-overdue-${r.property_id}`} className="pill pill--overdue w-fit"><WarningCircle size={12} weight="fill" />Overdue{r.aging_days > 0 ? ` · ${r.aging_days}d` : ""}</span>
              : <span className="pill pill--info">Outstanding</span>}
        {r.invoice_number && <div className="text-xs text-[#9A968E] mt-1 font-mono-num" data-testid={`billing-invnum-${r.property_id}`}>{r.invoice_number}</div>}
        {r.adj_total !== 0 && <div className="text-xs text-[#5C5A58] mt-0.5">{r.adj_total > 0 ? "+" : ""}{money(r.adj_total)} adj</div>}
      </td>
      <td className="p-4 text-right whitespace-nowrap">
        {!isAgent && (
          <Button data-testid={`billing-edit-${r.property_id}`} size="sm" variant="outline" onClick={() => openEdit(r)} className="border-[#E9DDC6] mr-2"><PencilSimple size={15} className="mr-1" />Edit</Button>
        )}
        {!isAgent && (
          <Button data-testid={`billing-adjust-${r.property_id}`} size="sm" variant="outline" onClick={() => openAdjust(r)} className="border-[#E9DDC6] mr-2"><Plus size={15} className="mr-1" />Adjust</Button>
        )}
        {!isAgent && (
          <Button data-testid={`billing-invoice-${r.property_id}`} size="sm" variant="outline" disabled={invoicing === r.property_id} onClick={() => downloadInvoice(r)} className="border-[#E9DDC6] mr-2"><FilePdf size={15} className="mr-1" />{invoicing === r.property_id ? "…" : "Invoice"}</Button>
        )}
        {!isAgent && r.paid && (
          <Button data-testid={`billing-refund-${r.property_id}`} size="sm" variant="outline" disabled={refunding === r.property_id} onClick={() => setRefundRow(r)} className="border-[#E9DDC6] text-[#4A0000] mr-2"><RotateCcw size={15} className="mr-1" />{refunding === r.property_id ? "…" : "Refund"}</Button>
        )}
        {!r.paid && (
          <Button data-testid={`pay-${r.property_id}`} size="sm" disabled={busy === r.property_id} onClick={() => startPay(r)} className={`${r.overdue ? "bg-[#E01E37] hover:bg-[#c01830]" : "bg-[#4A0000] hover:bg-[#5C1A1A]"} text-white`}><CreditCard size={16} className="mr-1" />{busy === r.property_id ? "Redirecting…" : "Pay with card"}</Button>
        )}
        {r.paid && isAgent && <span className="text-[#5C5A58] text-xs">—</span>}
      </td>
    </tr>
  );

  const groupByAgent = () => {
    const map = {};
    (data.incoming || []).forEach((r) => {
      const key = r.agent_id || r.agent || "unassigned";
      if (!map[key]) map[key] = { key, agent_id: r.agent_id, agent: r.agent || "Unassigned", rows: [], billed: 0, collected: 0, overdue: 0, outstanding: 0 };
      const g = map[key];
      g.rows.push(r);
      g.billed += r.net || 0;
      if (r.paid) g.collected += r.net || 0;
      else if (r.refunded) { /* excluded from balance owed */ }
      else if (r.overdue) g.overdue += r.net || 0;
      else g.outstanding += r.net || 0;
    });
    let arr = Object.values(map).map((g) => ({
      ...g, owed: g.overdue + g.outstanding, count: g.rows.length,
      oldestDue: g.rows.filter((r) => !r.paid && !r.refunded).map((r) => dueOf(r)).filter(Boolean).sort()[0] || null,
    }));
    const qq = invSearch.trim().toLowerCase();
    if (qq) arr = arr.filter((g) => (g.agent || "").toLowerCase().includes(qq));
    arr.sort((a, b) => b.owed - a.owed || b.billed - a.billed);
    return arr;
  };
  const agentGroups = viewMode === "agent" && !isAgent ? groupByAgent() : [];
  const toggleAgent = (key) => setExpanded((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });

  return (
    <div className="space-y-6" data-testid="billing-panel">
      <div className={`grid grid-cols-1 gap-6 ${isAgent ? "md:grid-cols-3" : "md:grid-cols-4"}`}>
        <Stat label="Total Balance" value={data.total} icon={CurrencyDollar} variant="oxblood" />
        {!isAgent && <Stat label="Collected" value={data.paid} icon={CheckCircle} variant="emerald" />}
        <Stat label="Overdue" value={data.overdue || 0} icon={WarningCircle} variant="crimson" />
        <Stat label="Outstanding" value={data.outstanding} icon={Clock} variant="bronze" />
      </div>
      <div className="flex items-center gap-2 flex-wrap" data-testid="billing-filter-bar">
        {!isAgent && (
          <div className="flex rounded-md border border-[#E9DDC6] overflow-hidden mr-1" data-testid="billing-viewmode">
            <button type="button" data-testid="billing-view-agent" onClick={() => setViewMode("agent")} className={`px-3 py-1.5 text-sm font-medium transition-colors ${viewMode === "agent" ? "bg-[#4A0000] text-white" : "bg-white text-[#5C5A58] hover:text-[#4A0000]"}`}>By agent</button>
            <button type="button" data-testid="billing-view-invoice" onClick={() => setViewMode("invoice")} className={`px-3 py-1.5 text-sm font-medium transition-colors ${viewMode === "invoice" ? "bg-[#4A0000] text-white" : "bg-white text-[#5C5A58] hover:text-[#4A0000]"}`}>By invoice</button>
          </div>
        )}
        <div className="relative">
          <MagnifyingGlass size={15} weight="duotone" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9A968E]" />
          <Input data-testid="billing-invoice-search" value={invSearch} onChange={(e) => setInvSearch(e.target.value)} placeholder={viewMode === "agent" && !isAgent ? "Search agent…" : "Search invoice # or property"} className="pl-8 w-[240px]" />
        </div>
        {(isAgent || viewMode === "invoice") && (
          <>
            <span className="text-sm text-[#5C5A58] flex items-center gap-1 ml-1"><CalendarBlank size={16} weight="duotone" />Due</span>
            <Select value={dueMode} onValueChange={setDueMode}>
              <SelectTrigger className="w-[170px]" data-testid="billing-due-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DUE_FILTERS.map((f) => <SelectItem key={f.value} value={f.value} data-testid={`billing-due-filter-${f.value}`}>{f.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" data-testid="billing-due-sort" onClick={() => setDueSort((s) => s === "asc" ? "desc" : s === "desc" ? "none" : "asc")} className="border-[#E9DDC6]">
              <ArrowsDownUp size={15} className="mr-1" />{dueSort === "asc" ? "Soonest first" : dueSort === "desc" ? "Latest first" : "Sort by due"}
            </Button>
          </>
        )}
        <span className="text-xs text-[#9A968E] ml-auto" data-testid="billing-visible-count">{viewMode === "agent" && !isAgent ? `${agentGroups.length} agents` : `${visibleRows.length} of ${data.incoming.length}`}</span>
      </div>
      {viewMode === "agent" && !isAgent ? (
        <div className="lux-panel overflow-x-auto">
          <table className="table-editorial text-sm min-w-[720px]" data-testid="billing-agent-rows">
            <thead>
              <tr className="bg-[#FAF9F6] text-left text-[#5C5A58]">
                <th className="p-4 font-medium">Agent</th>
                <th className="p-4 font-medium">Invoices</th>
                <th className="p-4 font-medium">Billed</th>
                <th className="p-4 font-medium">Collected</th>
                <th className="p-4 font-medium">Balance owed</th>
                <th className="p-4 font-medium">Oldest due</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {agentGroups.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-[#5C5A58]">No agents have been billed yet.</td></tr>}
              {agentGroups.map((g) => {
                const open = expanded.has(g.key);
                return (
                  <Fragment key={g.key}>
                    <tr className={`cursor-pointer hover:bg-[#FBFAF7] ${g.owed > 0 ? "border-l-4 border-[#E01E37]" : ""}`} onClick={() => toggleAgent(g.key)} data-testid={`agent-row-${g.agent_id || "unassigned"}`}>
                      <td className="p-4 font-medium">{g.agent}</td>
                      <td className="p-4">{g.count}</td>
                      <td className="p-4 font-mono-num">{money(g.billed)}</td>
                      <td className="p-4 font-mono-num text-[#2e7d32]">{money(g.collected)}</td>
                      <td className="p-4">
                        <span className={`font-mono-num font-semibold ${g.owed > 0 ? "text-[#4A0000]" : "text-[#5C5A58]"}`} data-testid={`agent-owed-${g.agent_id || "unassigned"}`}>{money(g.owed)}</span>
                        {g.overdue > 0 && <Badge className="ml-2 bg-[#E01E37]/10 text-[#E01E37] border-[#E01E37]/30">overdue {money(g.overdue)}</Badge>}
                      </td>
                      <td className="p-4">{g.oldestDue ? fmtDate(g.oldestDue) : <span className="text-[#9A968E]">—</span>}</td>
                      <td className="p-4 text-right whitespace-nowrap">
                        <Button size="sm" variant="outline" data-testid={`agent-statement-${g.agent_id || "unassigned"}`} className="border-[#E9DDC6] mr-2" onClick={(e) => { e.stopPropagation(); downloadStatement(g); }}><FilePdf size={14} className="mr-1" />Statement</Button>
                        {open ? <CaretUp size={16} className="inline text-[#5C5A58]" /> : <CaretDown size={16} className="inline text-[#5C5A58]" />}
                      </td>
                    </tr>
                    {open && (
                      <tr>
                        <td colSpan={7} className="p-0 bg-[#FBFAF7]">
                          <table className="table-editorial text-sm" data-testid={`agent-invoices-${g.agent_id || "unassigned"}`}>
                            <thead>
                              <tr className="text-left text-[#9A968E] text-xs">
                                <th className="px-4 py-2 font-medium">Property</th>
                                <th className="px-4 py-2 font-medium">Agent (owes)</th>
                                <th className="px-4 py-2 font-medium">Package price</th>
                                <th className="px-4 py-2 font-medium">Due</th>
                                <th className="px-4 py-2 font-medium">Status</th>
                                <th className="px-4 py-2 text-right font-medium">Action</th>
                              </tr>
                            </thead>
                            <tbody>{g.rows.map((r, i) => renderInvoiceRow(r, i))}</tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="lux-panel overflow-x-auto">
          <table className="table-editorial text-sm min-w-[720px]" data-testid="billing-rows">
            <thead>
              <tr className="bg-[#FAF9F6] text-left text-[#5C5A58]">
                <th className="p-4 font-medium">Property</th>
                <th className="p-4 font-medium">Agent (owes)</th>
                <th className="p-4 font-medium">Package price</th>
                <th className="p-4 font-medium">Due</th>
                <th className="p-4 font-medium">Status</th>
                <th className="p-4 text-right font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#5C5A58]">{data.incoming.length === 0 ? "No billable shoots yet. Choose a package type on a shoot." : "No invoices match your search or filter."}</td></tr>}
              {visibleRows.map((r, i) => renderInvoiceRow(r, i))}
            </tbody>
          </table>
        </div>
      )}

      {isAgent && <TestPaymentTools isAdmin={false} />}

      {/* Agent tip + pay dialog */}
      <Dialog open={!!payRow} onOpenChange={(o) => !o && setPayRow(null)}>
        <DialogContent data-testid="tip-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Complete your payment</DialogTitle>
            <DialogDescription>
              Add an optional tip for your photographer — 100% goes to them. Deliverables unlock once payment is complete.
            </DialogDescription>
          </DialogHeader>
          {payRow && (
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm border-b border-[#F0ECE2] pb-3">
                <span className="text-[#5C5A58]">{payRow.title}</span>
                <span className="font-mono-num font-semibold">{money(payRow.package_price)}</span>
              </div>
              <div>
                <p className="text-sm text-[#5C5A58] mb-2">Discount code <span className="font-normal text-xs">(optional)</span></p>
                <Input data-testid="checkout-discount-input" value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                  placeholder="e.g. SAVE20" className="font-mono-num uppercase" />
                {discChecking && <p className="text-xs text-[#5C5A58] mt-1" data-testid="checkout-discount-checking">Checking…</p>}
                {!discChecking && discInfo && (
                  <p data-testid="checkout-discount-message" className={`text-xs mt-1 ${discInfo.valid ? "text-green-700" : "text-red-600"}`}>{discInfo.message}</p>
                )}
              </div>
              {discInfo?.valid && (
                <div className="flex items-center justify-between text-sm text-[#2e7d32]" data-testid="checkout-discount-row">
                  <span>Discount {discountCode.trim().toUpperCase()}</span>
                  <span className="font-mono-num">−{money(discInfo.discount_amount)}</span>
                </div>
              )}
              {savedCards.length > 0 && (
                <div data-testid="saved-cards-section">
                  <p className="text-sm text-[#5C5A58] mb-2">Payment method</p>
                  <div className="space-y-2">
                    {savedCards.map((c) => (
                      <label key={c.id} data-testid={`saved-card-${c.id}`} className={`flex items-center gap-3 border rounded-md p-3 cursor-pointer transition-colors ${selectedPm === c.id ? "border-[#4A0000] bg-[#4A0000]/5" : "border-[#E9DDC6] hover:border-[#4A0000]/40"}`}>
                        <input type="radio" name="pm" checked={selectedPm === c.id} onChange={() => setSelectedPm(c.id)} className="accent-[#4A0000]" />
                        <CreditCard size={18} className="text-[#4A0000]" weight="duotone" />
                        <span className="capitalize text-sm font-medium">{c.brand} •••• {c.last4}</span>
                        <span className="ml-auto text-xs text-[#8A8782]">exp {c.exp_month}/{String(c.exp_year).slice(-2)}</span>
                      </label>
                    ))}
                    <label data-testid="saved-card-new" className={`flex items-center gap-3 border rounded-md p-3 cursor-pointer transition-colors ${selectedPm === "new" ? "border-[#4A0000] bg-[#4A0000]/5" : "border-[#E9DDC6] hover:border-[#4A0000]/40"}`}>
                      <input type="radio" name="pm" checked={selectedPm === "new"} onChange={() => setSelectedPm("new")} className="accent-[#4A0000]" />
                      <Plus size={18} className="text-[#4A0000]" weight="bold" />
                      <span className="text-sm font-medium">Use a new card</span>
                    </label>
                  </div>
                </div>
              )}
              <div>
                <p className="text-sm text-[#5C5A58] mb-2 flex items-center gap-1"><Heart size={15} weight="duotone" color="#B08D57" /> Add a tip</p>
                <div className="grid grid-cols-4 gap-2">
                  <button type="button" data-testid="tip-none" onClick={() => setTipChoice(0)}
                    className={`rounded-md border py-2 text-sm font-medium transition-colors ${tipChoice === 0 ? "bg-[#4A0000] text-white border-[#4A0000]" : "border-[#E9DDC6] text-[#5C5A58] hover:border-[#4A0000]"}`}>None</button>
                  {TIP_PRESETS.map((t) => (
                    <button key={t} type="button" data-testid={`tip-${t}`} onClick={() => setTipChoice(t)}
                      className={`rounded-md border py-2 text-sm font-medium transition-colors ${tipChoice === t ? "bg-[#4A0000] text-white border-[#4A0000]" : "border-[#E9DDC6] text-[#5C5A58] hover:border-[#4A0000]"}`}>${t}</button>
                  ))}
                </div>
                <div className="mt-2">
                  <button type="button" data-testid="tip-custom-toggle" onClick={() => setTipChoice("custom")}
                    className={`text-xs underline ${tipChoice === "custom" ? "text-[#4A0000]" : "text-[#5C5A58]"}`}>Enter a custom amount</button>
                  {tipChoice === "custom" && (
                    <div className="relative mt-2">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5A58]">$</span>
                      <Input data-testid="tip-custom-input" type="number" min="0" step="1" value={customTip}
                        onChange={(e) => setCustomTip(e.target.value)} placeholder="0" className="pl-7" />
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between border-t border-[#F0ECE2] pt-3">
                <span className="text-sm font-medium">Total</span>
                <span data-testid="tip-total" className="font-mono-num text-lg font-semibold text-[#4A0000]">{money(dialogNet + tipAmount)}</span>
              </div>
              <p className="flex items-center justify-center gap-1.5 text-xs text-[#9A968E]">
                <Lock size={13} weight="duotone" />Secure checkout — you'll finish on Stripe, then return here
              </p>
              {(savedCards.length === 0 || selectedPm === "new") && (
                <label className="flex items-center gap-2 text-sm cursor-pointer text-[#5C5A58]">
                  <input type="checkbox" data-testid="save-card-checkbox" checked={saveCard} onChange={(e) => setSaveCard(e.target.checked)} className="accent-[#4A0000] w-4 h-4" />
                  Save this card for faster checkout next time
                </label>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayRow(null)} data-testid="tip-cancel">Cancel</Button>
            <Button data-testid="tip-confirm-pay" onClick={confirmPay} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white">
              <CreditCard size={16} className="mr-1" />{selectedPm && selectedPm !== "new" ? `Pay ${money(dialogNet + tipAmount)} instantly` : `Pay ${money(dialogNet + tipAmount)}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin: edit incoming invoice */}
      {!isAgent && (
        <Dialog open={!!editRow} onOpenChange={(o) => !o && setEditRow(null)}>
          <DialogContent data-testid="billing-edit-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Edit invoice</DialogTitle>
              <DialogDescription>{editRow?.title} — adjust the client charge or mark it paid.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-[#5C5A58] mb-1">Package price ($)</p>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5A58]">$</span>
                  <Input data-testid="billing-edit-price" type="number" min="0" step="1" value={editForm.package_price} onChange={(e) => setEditForm((f) => ({ ...f, package_price: e.target.value }))} className="pl-7" />
                </div>
                {editRow?.discount_amount > 0 && <p className="text-xs text-[#5C5A58] mt-1">Discount {editRow.discount_code} −{money(editRow.discount_amount)} still applies to the net.</p>}
              </div>
              <div className="flex items-center justify-between border-t border-[#F0ECE2] pt-3">
                <span className="text-sm">Mark as paid (collected)</span>
                <Switch data-testid="billing-edit-paid" checked={editForm.client_paid} onCheckedChange={(v) => setEditForm((f) => ({ ...f, client_paid: v }))} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" data-testid="billing-edit-cancel" onClick={() => setEditRow(null)}>Cancel</Button>
              <Button data-testid="billing-edit-save" disabled={savingEdit} onClick={saveEdit} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white">{savingEdit ? "Saving…" : "Save changes"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Admin: refund confirm */}
      {!isAgent && (
        <Dialog open={!!adjustRow} onOpenChange={(o) => !o && setAdjustRow(null)}>
          <DialogContent data-testid="billing-adjust-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Adjust invoice</DialogTitle>
              <DialogDescription>{adjustRow?.title} — add a surcharge or discount line to this client invoice. A reason is required and logged.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                <button type="button" data-testid="adjust-type-surcharge" onClick={() => setAdjType("surcharge")}
                  className={`rounded-md border py-2 text-sm font-medium transition-colors ${adjType === "surcharge" ? "bg-[#4A0000] text-white border-[#4A0000]" : "border-[#E9DDC6] text-[#5C5A58]"}`}>Surcharge (+)</button>
                <button type="button" data-testid="adjust-type-discount" onClick={() => setAdjType("discount")}
                  className={`rounded-md border py-2 text-sm font-medium transition-colors ${adjType === "discount" ? "bg-[#4A0000] text-white border-[#4A0000]" : "border-[#E9DDC6] text-[#5C5A58]"}`}>Discount (−)</button>
              </div>
              <div>
                <p className="text-sm text-[#5C5A58] mb-1">Amount ($)</p>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5A58]">$</span>
                  <Input data-testid="adjust-amount" type="number" min="0" step="0.01" value={adjAmount} onChange={(e) => setAdjAmount(e.target.value)} className="pl-7" />
                </div>
              </div>
              <div>
                <p className="text-sm text-[#5C5A58] mb-1">Reason (required)</p>
                <Input data-testid="adjust-reason" value={adjReason} onChange={(e) => setAdjReason(e.target.value)} placeholder="e.g. rush delivery, twilight add-on, loyalty credit" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" data-testid="adjust-cancel" onClick={() => setAdjustRow(null)}>Cancel</Button>
              <Button data-testid="adjust-confirm" disabled={savingAdj} onClick={doAdjust} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white">{savingAdj ? "Applying…" : "Apply adjustment"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Admin: refund confirm */}
      {!isAgent && (
        <Dialog open={!!refundRow} onOpenChange={(o) => !o && setRefundRow(null)}>
          <DialogContent data-testid="billing-refund-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Refund this invoice?</DialogTitle>
              <DialogDescription>
                {refundRow?.title} — {money(Math.max(0, (refundRow?.package_price || 0) - (refundRow?.discount_amount || 0)))} will be refunded to the client and the invoice reopened.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" data-testid="billing-refund-cancel" onClick={() => setRefundRow(null)}>Cancel</Button>
              <Button data-testid="billing-refund-confirm" disabled={!!refunding} onClick={doRefund} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white"><RotateCcw size={16} className="mr-1.5" />{refunding ? "Refunding…" : "Refund payment"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
